import React, { useState, type FormEvent } from "react";
import { observer } from "mobx-react";
import { FusionBody } from "@/components/ui/fusion-body";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import elofleetLogoUrl from "../../images/elofleet-logo.svg";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { LanguageSelector } from "@/parts/language-selector";
import { ShowcasePanel } from "@/parts/showcase";
import { Brands } from "@/parts/brands";
import { cn } from "@/lib/utils";
import { store } from "../../store";
import { LoginBackground } from "@/parts/login-background";
import { Footer } from "@/parts/footer";
import { NotACustomerYet } from "@/parts/not-a-customer-yet";
import { bookDemoUrl } from "../../utils/constants";

export const PageDispatcher = observer((): JSX.Element => {
    const t = store.i18n.t;
    const [instanceName, setInstanceName] = useState<string>("");
    const [badInstance, setBadInstance] = useState(false);
    const [loading, setLoading] = useState(false);

    const check = async (ev: FormEvent): Promise<void> => {
        ev.preventDefault();

        if (!instanceName) {
            setBadInstance(true);
            return;
        }
        setLoading(true);
        // The backend /health route sets `Access-Control-Allow-Origin: *`. This way we
        // are able to check the status of other existent instances by fetching that route.
        // A bad URL will typically return 404 but the fetch client is not allowed to query
        // that resource due to missing CORS headers.
        const instance = `https://${instanceName}.elofleet.com`;
        try {
            const res = await fetch(`${instance}/health`);
            const bad = res.status === 404;
            setBadInstance(bad);
            if (!bad) {
                window.location.assign(`${instance}/login`);
            }
        } catch {
            // Catch the CORS error, which indicates a non existent instance.
            setBadInstance(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <FusionBody className="bg-transparent">
            <LoginBackground />
            <div className="flex h-full min-h-[800px] flex-col">
                <div className="flex grow">
                    <div className="flex w-full shrink-0 items-center justify-center p-4 sm:p-6 lg:w-[500px] 2xl:w-[43%] 2xl:justify-end 2xl:pr-[5%]">
                        <div className="w-full max-w-[420px] rounded-2xl bg-white p-4 shadow-lg sm:p-8">
                            <a
                                href="https://elofleet.com"
                                target="_blank"
                                rel="noreferrer"
                                className="lg:hidden"
                            >
                                <img
                                    src={elofleetLogoUrl}
                                    alt="ELOfleet logo"
                                    className="mb-4 h-9 w-auto object-contain"
                                />
                            </a>
                            <div className="mb-8 flex items-center justify-between">
                                <LanguageSelector />
                                <Button variant="outline" asChild>
                                    <a href={bookDemoUrl} target="_blank" rel="noreferrer">
                                        {t("page.dispatcher.bookDemoButton.text")}
                                    </a>
                                </Button>
                            </div>
                            <h2 className="text-2xl font-bold">{t("page.dispatcher.title")}</h2>
                            <p className="text-muted-foreground mt-1 mb-6 text-sm">
                                {t("page.dispatcher.subtitle")}
                            </p>
                            {badInstance && (
                                <Alert variant="destructive" className="mb-4">
                                    <AlertTitle>
                                        {t("page.dispatcher.workspaceNotFound")}
                                    </AlertTitle>
                                    <AlertDescription>
                                        {t("page.dispatcher.badInstanceName")}
                                    </AlertDescription>
                                </Alert>
                            )}
                            <form className="flex flex-col gap-2" onSubmit={check}>
                                <Label htmlFor="instance">{t("page.dispatcher.input.label")}</Label>
                                <div
                                    className={cn(
                                        "border-input focus-within:ring-ring flex h-9 w-full items-center rounded-md border bg-transparent shadow-xs transition-colors focus-within:ring-1",
                                        badInstance && "border-red-500 focus-within:ring-red-500",
                                    )}
                                >
                                    <Input
                                        id="instance"
                                        autoFocus
                                        placeholder={t("page.dispatcher.input.placeholder")}
                                        value={instanceName}
                                        onChange={(ev) => {
                                            setInstanceName(ev.target.value);
                                            if (badInstance) setBadInstance(false);
                                        }}
                                        className="h-full flex-1 border-none shadow-none focus-visible:ring-0"
                                    />
                                    <span className="text-muted-foreground flex h-8.5 items-center justify-center rounded-r-md bg-neutral-200 px-3 text-sm">
                                        .elofleet.com
                                    </span>
                                </div>

                                <Button className="mt-4 w-full" type="submit" disabled={loading}>
                                    {t("page.dispatcher.button.continue")}
                                </Button>
                            </form>
                            <NotACustomerYet className="mt-5" />
                            <Footer className="mt-6" />
                        </div>
                    </div>
                    <ShowcasePanel />
                </div>
                <Brands />
            </div>
        </FusionBody>
    );
});
